import _ from 'lodash';
import { filterByExpiryDate } from '../../hooks/knowledge.hooks';
import { normalizedContains, removeDiacritics } from '../../utils/Utils';
import { filterByTags } from 'shared/utils/search.utils';
import { getContacts } from './contact.selector';
import Fuse from 'fuse.js';
import { isImprovedSearchEnabled } from './features.selector';
import { SEARCH_TYPES_KEYS, CATEGORY_TYPES } from '../../constants';

const SHOW_SCORE = false;

// Search for knowledge item
const searchOnKnowledgeItem = ({ title, subtitle, keywords = [] }, term) => {
  return (
    normalizedContains(title, term) ||
    normalizedContains(subtitle, term) ||
    !!keywords.find(key => normalizedContains(key, term))
  );
};

// Search for lab item
const searchOnLabItem = ({ title, value, keywords = [] }, term) => {
  return (
    normalizedContains(title, term) ||
    normalizedContains(value, term) ||
    !!keywords.find(key => normalizedContains(key, term))
  );
};

// Search for admission item
const searchOnAdmissionCategories = ({ title, value, keywords = [] }, term) => {
  return normalizedContains(title, term) || !!keywords.find(key => normalizedContains(key, term));
};

// Search for contacts
export const searchOnContactList = ({ firstName, lastName, email, department }, term) => {
  return (
    normalizedContains(firstName, term) ||
    normalizedContains(lastName, term) ||
    normalizedContains(email, term) ||
    normalizedContains(department, term)
  );
};

/**
 * Search for term in results
 * @param {Object[]} results
 * @param {String} term
 * @param {Object} state
 * @returns {{
 *  knowledge: Object[],
 *  lab: Object[],
 *  admission: Object[],
 *  contacts: Object[],
 * }}
 */
export const innerSearch = (results = [], term, state = {}) => {
  let termSplit = term.split(' ');

  for (let i = 1; i < termSplit.length; i++) {
    if (results.length === 0) return results;
    results.lab = results.lab.filter(item => searchOnLabItem(item, termSplit[i]));
    results.knowledge = results.knowledge.filter(item => searchOnKnowledgeItem(item, termSplit[i]));
    results.admission = results.admission.filter(item =>
      searchOnAdmissionCategories(item, termSplit[i]),
    );

    const departmentLang = state?.lang?.['TAGS'];
    results.contacts = results.contacts?.filter(item =>
      searchOnContactList(
        { ...item, department: departmentLang[item?.professional?.department] },
        termSplit[i],
      ),
    );
  }

  return results;
};

/**
 * Create a map of tags and their occurrences
 * @param {{
 *  specialty: { [key: string]: number },
 *  subspecialty: { [key: string]: number },
 *  contentType: { [key: string]: number },
 *  targetAudience: { [key: string]: number },
 *  labels: { [key: string]: number },
 * }} map
 * @param {{
 *  specialty: String,
 *  subspecialty: String,
 *  contentType: String,
 *  targetAudience: String[],
 *  labels: String[],
 * }} [tags={}]
 */
const addToMap = (map, tags = {}) => {
  Object.keys(tags).forEach(key => {
    const value = tags[key];
    if (!value) return;

    if (_.isArray(value)) {
      value.forEach(arrKey => {
        if (!map[key][arrKey]) {
          map[key][arrKey] = 0;
        }
        map[key][arrKey]++;
      });
      return;
    }

    if (!map[key][value]) {
      map[key][value] = 0;
    }
    map[key][value]++;
  });
};

/**
 * Old search mechanism using regex and inner search
 *
 * @param {Object} state
 * @param {String} term
 * @param {{}} filters
 * @return {{
 *  initialResults: Object,
 *  results: Object[],
 * }}
 */
const oldSearchMechanism = (state, term, filters) => {
  const knowledge = state.knowledge?.items;
  const lab = state.lab?.items;
  const admission = state.admission?.categories;
  const contacts = getContacts(state);
  const termSplit = removeDiacritics(term).split(' ');
  const tagsMap = {
    specialty: {},
    subspecialty: {},
    contentType: {},
    targetAudience: {},
    labels: {},
  };
  let results = {};

  const filtersByTags = filterByTags({ filters });

  // Search for knowledge items
  results.knowledge = knowledge.filter(knowledgeItem =>
    searchOnKnowledgeItem(knowledgeItem, termSplit[0]),
  );

  // Search for lab items
  results.lab = lab.filter(labItem => searchOnLabItem(labItem, termSplit[0]));

  // Search for admission items
  results.admission = admission.filter(admissionCategories =>
    searchOnAdmissionCategories(admissionCategories, termSplit[0]),
  );

  // Search for contacts

  const departmentLang = state.lang['TAGS'];
  results.contacts = contacts?.filter(contact =>
    searchOnContactList(
      { ...contact, department: departmentLang[contact?.professional?.department] },
      termSplit[0],
    ),
  );

  const initialResults = _.clone(results);

  results = innerSearch(results, term, state);

  // Map tags
  results.knowledge
    .filter(filterByExpiryDate)
    .filter(filtersByTags)
    .forEach(item => addToMap(tagsMap, { ...item?.tags, labels: item?.labels }));

  results.lab.filter(filtersByTags).forEach(item => addToMap(tagsMap, item?.tags));

  results.admission.filter(filtersByTags).forEach(item => addToMap(tagsMap, item?.tags));

  // Map results with type and item
  results.knowledge = results.knowledge.map(item => ({
    item,
    type: SEARCH_TYPES_KEYS.documents,
  }));

  results.lab = results.lab.map(item => ({
    item,
    type: SEARCH_TYPES_KEYS.compendium,
  }));

  results.admission = results.admission
    ?.filter(item => item?.type !== CATEGORY_TYPES.CATEGORY)
    .map(item => ({
      item,
      type: SEARCH_TYPES_KEYS.admission,
    }));

  results.contacts = results.contacts.map(item => ({
    item,
    type: SEARCH_TYPES_KEYS.contacts,
  }));

  results.tagsMap = tagsMap;

  return { initialResults, results };
};

/**
 * Improved search mechanism using Fuse.js
 *
 * @param {Object} state
 * @param {String} term
 * @param {{}} filters
 * @return {{
 *  initialResults: Object,
 *  results: Object[],
 * }}
 */
const improvedSearchMechanism = (state, term, filters) => {
  const departmentLang = state.lang['TAGS'];

  // Map tags
  const tagsMap = {
    specialty: {},
    subspecialty: {},
    contentType: {},
    targetAudience: {},
    labels: {},
  };

  const results = {};

  // Get contacts and map department
  const contacts = getContacts(state).map(contact => ({
    ...contact,
    department: departmentLang[contact?.professional?.department],
  }));

  // Get search config
  const { IMPROVED_SEARCH_CONFIG = {} } = state.generalConfig;

  // Fuse search
  const knowledgeFuse = new Fuse(state.knowledge.items, IMPROVED_SEARCH_CONFIG?.KNOWLEDGE || {});
  const labFuse = new Fuse(state.lab.items, IMPROVED_SEARCH_CONFIG?.LAB || {});
  const admissionFuse = new Fuse(
    state.admission?.categories?.filter(item => item?.type !== CATEGORY_TYPES.CATEGORY),
    IMPROVED_SEARCH_CONFIG?.ADMISSION || {},
  );
  const contactsFuse = new Fuse(contacts, IMPROVED_SEARCH_CONFIG?.CONTACTS || {});

  // Prepare filters
  const filtersByTags = filterByTags({ filters });

  // Convert score to percentage with 3 decimals
  const convertScoreToPercentage = score => {
    return (score * 100).toFixed(3);
  };

  // Search for knowledge items
  const knowledgeResults = knowledgeFuse.search(term);
  results.knowledge = knowledgeResults.map(({ item, score }) => ({
    item: SHOW_SCORE
      ? {
          ...item,
          title: `${item.title} (${convertScoreToPercentage(score)})`,
        }
      : item,
    score,
    type: SEARCH_TYPES_KEYS.documents,
  }));

  // Search for lab items
  results.lab = labFuse.search(term).map(({ item, score }) => ({
    item: SHOW_SCORE
      ? {
          ...item,
          title: `${item.title} (${convertScoreToPercentage(score)})`,
        }
      : item,
    type: SEARCH_TYPES_KEYS.compendium,
    score,
  }));

  // Search for admission items
  results.admission = admissionFuse.search(term).map(({ item, score }) => ({
    item: SHOW_SCORE
      ? {
          ...item,
          title: `${item.title} (${convertScoreToPercentage(score)})`,
        }
      : item,
    type: SEARCH_TYPES_KEYS.admission,
    score,
  }));

  // Search for contacts
  results.contacts = contactsFuse.search(term).map(({ item, score }) => ({
    item: SHOW_SCORE
      ? {
          ...item,
          firstName: `${item.firstName} (${convertScoreToPercentage(score)})`,
        }
      : item,
    type: SEARCH_TYPES_KEYS.contacts,
    score,
  }));

  const initialResults = _.clone(results);

  // Map tags
  results.knowledge
    .filter(({ item }) => filterByExpiryDate(item) && filtersByTags(item))
    .forEach(({ item }) => addToMap(tagsMap, { ...item?.tags, labels: item?.labels }));

  results.lab
    .filter(({ item }) => filtersByTags(item))
    .forEach(({ item }) => addToMap(tagsMap, item?.tags));

  results.admission
    .filter(({ item }) => filtersByTags(item))
    .forEach(({ item }) => addToMap(tagsMap, item?.tags));

  results.tagsMap = tagsMap;

  return { initialResults, results };
};

/**
 * Search for term in knowledge, lab, admission and contacts
 * @param {Object} state
 * @param {String} term
 * @param {Object} filters
 * @returns {{
 * initialResults: {
 *  knowledge: Object[],
 *  lab: Object[],
 *  admission: Object[],
 *  contacts: Object[],
 * },
 * results: {
 *  knowledge: Object[],
 *  lab: Object[],
 *  admission: Object[],
 *  contacts: Object[],
 *  tagsMap: {
 *    specialty: { [key: string]: number },
 *    subspecialty: { [key: string]: number },
 *    contentType: { [key: string]: number },
 *    targetAudience: { [key: string]: number },
 *    labels: { [key: string]: number },
 *    },
 *  extendedSearch?: {
 *    knowledge: Object[],
 *    lab: Object[],
 *    admission: Object[],
 *    contacts: Object[],
 *    tagsMap: {
 *      specialty: { [key: string]: number },
 *      subspecialty: { [key: string]: number },
 *      contentType: { [key: string]: number },
 *      targetAudience: { [key: string]: number },
 *      labels: { [key: string]: number },
 *      },
 *    }
 *  },
 * }}
 */
export const search = (state, term, filters) => {
  // Get the feature flag
  const isImprovedSearch = isImprovedSearchEnabled(state);

  // Generate old search results
  const oldResults = oldSearchMechanism(state, term, filters);

  // Combine results
  if (isImprovedSearch) {
    // Generate improved search results on demand
    const improveResults = improvedSearchMechanism(state, term, filters);

    oldResults.results.extendedSearch = improveResults.results;

    // Remove duplicate on extended search based on old search results
    oldResults.results.extendedSearch.knowledge = _.differenceBy(
      oldResults.results.extendedSearch.knowledge,
      oldResults.results.knowledge,
      'item.id',
    );

    oldResults.results.extendedSearch.lab = _.differenceBy(
      oldResults.results.extendedSearch.lab,
      oldResults.results.lab,
      'item.id',
    );

    oldResults.results.extendedSearch.admission = _.differenceBy(
      oldResults.results.extendedSearch.admission,
      oldResults.results.admission,
      'item.id',
    );

    oldResults.results.extendedSearch.contacts = _.differenceBy(
      oldResults.results.extendedSearch.contacts,
      oldResults.results.contacts,
      'item.id',
    );

    oldResults.results.tagsMap = _.mergeWith(
      oldResults.results.tagsMap,
      improveResults.results.tagsMap,
      (obj, src) => {
        if (_.isNumber(obj)) {
          return obj + src;
        }
      },
    );
  }

  return oldResults;
};

export const isOnSearch = ({ search }) => {
  return search.isOnSearch;
};
